import React, { useEffect } from "react";

import dayjs from "dayjs";
import Spinner from "react-bootstrap/Spinner";
import { useTranslation } from "react-i18next";

/** Re-reads while a synchronization runs, so the status changes by itself when it ends. */
export const useSyncPolling = (
  running: boolean,
  {
    startPolling,
    stopPolling,
  }: { startPolling: (ms: number) => void; stopPolling: () => void },
) => {
  useEffect(() => {
    if (running) {
      startPolling(5000);
    }

    return () => stopPolling();
  }, [running, startPolling, stopPolling]);
};

/** Running, last synchronization with what it did, or the failure (error on hover). */
const SyncStatus = ({ item }: { item: IActiveDirectory }) => {
  const { t } = useTranslation(["client"]);

  if (item.syncRunning) {
    return (
      <span className="text-primary">
        <Spinner animation="border" size="sm" className="mr-2" />
        {t("client:client.integrations.activeDirectory.sync.running")}
      </span>
    );
  }

  if (item.failedAttempts && item.lastSyncError) {
    return (
      <span className="text-danger" title={item.lastSyncError}>
        {t("client:client.integrations.activeDirectory.sync.failing", {
          count: item.failedAttempts,
          since: item.lastSyncErrorAt
            ? dayjs(item.lastSyncErrorAt).format("L LT")
            : "—",
        })}
      </span>
    );
  }

  if (item.lastSyncAt) {
    const counts = item.lastSyncCounts;

    return (
      <>
        {dayjs(item.lastSyncAt).format("L LT")}
        {counts && (
          <small
            className={`d-block ${
              counts.failed ? "text-danger" : "text-muted"
            }`}
          >
            {t("client:client.integrations.activeDirectory.sync.counts", {
              added: counts.added ?? 0,
              updated: counts.updated ?? 0,
              removed: counts.removed ?? 0,
              failed: counts.failed ?? 0,
            })}
          </small>
        )}
      </>
    );
  }

  return (
    <span className="text-muted">
      {t("client:client.integrations.activeDirectory.sync.never")}
    </span>
  );
};

export default SyncStatus;
